"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import Image, { type StaticImageData } from "next/image";
import { useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import type { SolutionPageContent } from "@/app/servicos/modernizacao-de-sistemas-legados/content/content";
import DifferencesLeft from "~/public/assets/backgrounds/bg-flow-left.png";
import DifferencesRight from "~/public/assets/backgrounds/bg-flow-right.png";
import DifferenceDots from "~/public/assets/services/differences-dots-card.png";
import { useServiceCardsAnimation } from "../services-pages/use-service-cards-animation";

type ServicePageTemplateProps = {
	solutions: SolutionPageContent;
};

type BenefitsItemCardProps = {
	cards: {
		image: StaticImageData;
		title: string;
		description: string;
	};
};

function BenefitsItemCard({ cards }: BenefitsItemCardProps) {
	return (
		<div
			data-service-card
			className="relative h-full! flex flex-col pt-0 bg-[#11111180] rounded-2xl"
		>
			<Image
				src={DifferenceDots}
				alt="Fundo de pontos para decoração"
				className="w-full max-w-60 absolute right-0 bottom-0 scale-x-[-1] scale-y-[-1] z-10"
				width={2000}
				height={2000}
			/>
			<Image
				data-service-card-media
				src={cards.image}
				alt={cards.title}
				className="w-full h-fit! relative z-20 mt-1"
			/>
			<div className="pt-1 p-10.5 flex flex-col gap-3">
				<p className="text-base md:text-xl font-medium whitespace-pre-line">
					{cards.title}
				</p>
				<p className="max-md:text-sm text-[#AAAAAA] whitespace-pre-line">
					{cards.description}
				</p>
			</div>
		</div>
	);
}

export default function BenefitsServicesSection({
	solutions,
}: ServicePageTemplateProps) {
	const sectionRef = useRef<HTMLElement | null>(null);

	useServiceCardsAnimation(sectionRef, {
		preset: "drift",
	});

	return (
		<section
			ref={sectionRef}
			className="md:container-padding relative text-white bg-gray-1100"
		>
			<Image
				src={DifferencesLeft}
				alt=""
				aria-hidden="true"
				className="hidden 2xl:block h-auto w-full max-w-[8%] 3xl:max-w-[9.7%] max-h-[70%] top-0 left-0 absolute"
			/>
			<Image
				src={DifferencesRight}
				alt=""
				aria-hidden="true"
				className="hidden 2xl:block h-auto w-full max-w-[8.5%] 3xl:max-w-[9.7%] max-h-[70%] top-0 right-0 absolute"
			/>

			<div className="container md:relative md:border-x md:border-gray-300 px-6 lg:px-18 3xl:px-24! py-18 lg:py-22">
				<span className="hidden md:inline-flex absolute -top-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />
				<span className="hidden md:inline-flex absolute -top-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />

				<div className="flex flex-col text-center">
					<p className="tag">{solutions.benefits?.tag}</p>
					<h2 className="title">{solutions.benefits?.title}</h2>
					<p className="description mt-4 max-w-150 mx-auto">
						{solutions.benefits?.description}
					</p>
				</div>

				<div
					data-service-cards-animation-trigger
					className="block md:hidden"
				>
					<Swiper
						modules={[A11y, Navigation]}
						navigation={{
							prevEl: ".comparison-prev",
							nextEl: ".comparison-next",
						}}
						slidesPerView="auto"
						spaceBetween={28}
						className="comparison-swiper mt-10 overflow-visible! [&_.swiper-wrapper]:h-fit!"
					>
						{solutions.benefits?.cards?.map((cards) => (
							<SwiperSlide
								key={cards.image.src}
								className="h-auto! max-w-[92%]"
							>
								<BenefitsItemCard cards={cards} />
							</SwiperSlide>
						))}
					</Swiper>
					<div className="mt-5 flex items-center gap-5 relative z-20">
						<button
							aria-label="Item anterior"
							className="comparison-prev cursor-pointer text-white transition-all duration-300 ease-in-out"
							type="button"
						>
							<ArrowLeft
								className="size-9 text-primary-300"
								strokeWidth={1.5}
							/>
						</button>
						<button
							aria-label="Próximo item"
							className="comparison-next cursor-pointer text-white transition-all duration-300 ease-in-out"
							type="button"
						>
							<ArrowRight
								className="size-9 text-primary-300"
								strokeWidth={1.5}
							/>
						</button>
					</div>
				</div>

				<div
					data-service-cards-animation-trigger
					className="mt-16 md:grid md:grid-cols-2 2xl:grid-cols-3! gap-8 hidden"
				>
					{solutions.benefits?.cards?.map((cards) => (
						<BenefitsItemCard key={cards.image.src} cards={cards} />
					))}
				</div>
			</div>
		</section>
	);
}
